import { trpc, trpcVanilla } from "@client/trpc";
import type { QueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { beginIdentityScopedPrefetch } from "./prefetchIdentityGuard";
import { impersonationRef } from "@client/ImperativeState";
import {
  type LeadClipHintScope,
  readCachedCurrentUser,
  readLeadClipVideoIdHint,
} from "@client/offline/offlineCache";
import {
  proposalVideoMediaInput,
  workspaceProposalClipsInputForPath,
  type VideoMediaInput,
} from "@lib/funderWorkspaceRoutes";

type ClipsWithVideoIds = {
  excerpts: Array<{ videoId: string }>;
};

// ClipsPlayer leads its list with the uploaded excerpts and selects the first
// clip. Mirror that so the media URL we warm is the one the initial player will
// request.
export function initialClipVideoId(data: ClipsWithVideoIds): string | null {
  return data.excerpts[0]?.videoId ?? null;
}

/**
 * The exact query useVideoSource mounts for a video's signed media URLs
 * (shared key + staleTime), so entries warmed by the startup prefetches are
 * consumed by the player instead of duplicated.
 */
export function videoMediaQueryOptions(mediaInput: VideoMediaInput) {
  return {
    queryKey: getQueryKey(trpc.getVideoMediaUrls, mediaInput, "query"),
    queryFn: () => trpcVanilla.getVideoMediaUrls.query(mediaInput),
    staleTime: Infinity,
  };
}

/**
 * The effective viewer for lead-clip hint reads, derived without touching the
 * network: the impersonation ref, else the identity cached by the last
 * successful getCurrentUser (the same localStorage entry first paint renders
 * from — see useCurrentUser). A fresh browser has neither — and no hints
 * either, so skipping the speculative warm there loses nothing.
 */
export function hintViewerUserId(): string | null {
  return impersonationRef.current ?? readCachedCurrentUser()?.userId ?? null;
}

/**
 * The lead-video hint for a list scope, read at render time by the workspace
 * panels: while the list query is in flight, the panel pre-mounts the player
 * for this video instead of a bare skeleton box, so the player chunk, media
 * element, and first media bytes can warm before the list arrives. The
 * pathname-derived media query resolves the same lead independently. A stale
 * hint briefly mounts the wrong video, but useVideoSource verifies the
 * resolver's returned video id before serving it.
 */
export function hintedLeadClipVideoId(
  roundId: string,
  scopeId: string,
): string | null {
  const viewerUserId = hintViewerUserId();
  if (viewerUserId == null) return null;
  return readLeadClipVideoIdHint({
    roundId,
    scopeId,
    effectiveViewerUserId: viewerUserId,
  });
}

/**
 * Starts a direct signed-media request from a remembered selection. Used for
 * query-string targets, whose video identity cannot be derived from pathname
 * alone. Returns the hinted video id, or null when there is no usable hint.
 */
export function warmHintedLeadClipMedia(
  queryClient: QueryClient,
  hintScope: LeadClipHintScope | null,
): string | null {
  if (hintScope == null) return null;
  const hintedVideoId = readLeadClipVideoIdHint(hintScope);
  if (hintedVideoId == null) return null;
  void queryClient.prefetchQuery(
    videoMediaQueryOptions({ videoId: hintedVideoId }),
  );
  return hintedVideoId;
}

export function workspaceExcerptTargetFromSearch(
  search: string,
): string | null {
  return new URLSearchParams(search).get("excerpt");
}

// On a proposal deep link, start the clips request and pathname-derived media
// request together. Cold loads adopt the HTML shell's in-flight media request;
// in-document navigation starts the same key before the panel mounts.
export function prefetchWorkspaceProposalClips(
  queryClient: QueryClient,
  roundId: string,
  pathname: string,
  targetExcerptVideoId: string | null = null,
): void {
  const input = workspaceProposalClipsInputForPath(roundId, pathname);
  if (input == null) return;
  const initialMediaInput = proposalVideoMediaInput(roundId, input.proposalId);

  void (async () => {
    const guard = beginIdentityScopedPrefetch(queryClient);
    try {
      const viewerUserId = hintViewerUserId();
      const hintScope: LeadClipHintScope | null =
        viewerUserId == null
          ? null
          : {
              roundId,
              scopeId: input.proposalId,
              effectiveViewerUserId: viewerUserId,
            };
      const hintedVideoId =
        targetExcerptVideoId == null
          ? null
          : warmHintedLeadClipMedia(queryClient, hintScope);
      if (hintedVideoId != null) {
        // Speculative, but still this chain's entry to clean up if the viewer
        // switches while it is in flight.
        guard.warm(videoMediaQueryOptions({ videoId: hintedVideoId }).queryKey);
      } else if (targetExcerptVideoId == null) {
        // This exact request was already started by the HTML shell on a cold
        // load; fetchWithEarlyFetches adopts it here. In-document navigation
        // starts the same key before the destination panel mounts.
        const mediaOptions = videoMediaQueryOptions(initialMediaInput);
        guard.warm(mediaOptions.queryKey);
        void queryClient.prefetchQuery(mediaOptions);
      }

      const clips = await queryClient.fetchQuery({
        queryKey: guard.warm(
          getQueryKey(trpc.getProposalTranscriptClips, input, "query"),
        ),
        queryFn: () => trpcVanilla.getProposalTranscriptClips.query(input),
      });
      if (guard.discardIfViewerChanged()) return;
      const videoId =
        targetExcerptVideoId != null &&
        clips.excerpts.some(
          (excerpt) => excerpt.videoId === targetExcerptVideoId,
        )
          ? targetExcerptVideoId
          : initialClipVideoId(clips);
      if (videoId == null) return;

      const mediaOptions = videoMediaQueryOptions(
        targetExcerptVideoId == null ? initialMediaInput : { videoId },
      );
      guard.warm(mediaOptions.queryKey);
      await queryClient.prefetchQuery(mediaOptions);
      guard.discardIfViewerChanged();
    } catch {
      // This is an optimization only. The mounted hooks retain their normal
      // loading and error behavior if either early request fails.
    }
  })();
}
